import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteShareLink,
  getPublicShare,
  getShareLink,
  putShareLink,
} from "@/services/DataService";
import type { ShareEntityType, ShareLink } from "@/types/share";

/** How long a read link is reused: the popover's own writes refresh the cache. */
const SHARE_LINK_STALE_MS = 30_000;

const shareLinkKey = (entityType: ShareEntityType, entityId: string) =>
  ["share-link", entityType, entityId] as const;

/**
 * An entity's public link, and the three things a member can do with it:
 * share (or move it to another version) and stop sharing.
 *
 * Read only while the caller asks for it, which the Share popover does when it
 * opens: a page with a Share control nobody touches costs no request. Both
 * writes put their answer straight into the cache, so the popover shows the
 * new state without reading again, and a read is reused for a short while
 * rather than repeated on every open and every switch back to the Share tab.
 */
export function useShareLink(
  entityType: ShareEntityType,
  entityId: string,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const key = shareLinkKey(entityType, entityId);

  const query = useQuery({
    queryKey: key,
    queryFn: () => getShareLink(entityType, entityId),
    enabled: enabled && Boolean(entityId),
    staleTime: SHARE_LINK_STALE_MS,
  });

  const share = useMutation({
    mutationFn: (version?: number) => putShareLink(entityType, entityId, version),
    onSuccess: (link: ShareLink) => {
      queryClient.setQueryData(key, link);
    },
  });

  const revoke = useMutation({
    mutationFn: () => deleteShareLink(entityType, entityId),
    onSuccess: () => {
      queryClient.setQueryData(key, null);
    },
  });

  return { query, share, revoke };
}

/**
 * What a public link shows. A link that is gone answers 404, which the app's
 * query defaults never retry, so the not-found state shows at once.
 */
export function usePublicShare(slug: string) {
  return useQuery({
    queryKey: ["public-share", slug],
    queryFn: () => getPublicShare(slug),
    enabled: Boolean(slug),
  });
}
